import type { State } from '../core/state';
import { absoluteUrl, now, randomId } from '../core/util';
import type { Exchange, RecentRequest, WidgetEvent } from '../types';
import type { Emit } from './errors';

export const TRACE_HEADER = 'X-FE-Trace-Id';
const TEXT_TYPES = /^(application\/(json|x-www-form-urlencoded|xml|problem\+json)|text\/)/i;

interface Captured {
  method: string;
  url: string;
  status: number;
  duration_ms: number;
  trace_id?: string;
  request?: Exchange;
  response?: Exchange;
  error?: string;
}

/** Monkey-patches fetch and XMLHttpRequest: X-FE-Trace-Id + capture (4xx/5xx/network failure → http_error). */
export function installNetworkCollector(state: State, emit: Emit): () => void {
  const originalFetch = window.fetch;
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetHeader = XMLHttpRequest.prototype.setRequestHeader;

  function record(c: Captured): void {
    const at = now();
    const rec: RecentRequest = {
      method: c.method,
      url: c.url,
      status: c.status,
      duration_ms: c.duration_ms,
      trace_id: c.trace_id,
      at,
      request: c.request,
      response: c.response,
    };
    state.requests.push(rec);
    state.breadcrumbs.push({ type: 'http', method: c.method, url: c.url.slice(0, 500), status: c.status, duration_ms: c.duration_ms, at });

    if (c.status >= 400 || c.status === 0) {
      emit({
        type: 'http_error',
        level: c.status >= 500 || c.status === 0 ? 'error' : 'warning',
        message: c.error ?? `HTTP ${c.status}`,
        url: c.url,
        method: c.method,
        status_code: c.status,
        duration_ms: c.duration_ms,
        trace_id: c.trace_id,
        request: c.request,
        response: c.response,
        breadcrumbs: state.breadcrumbs.last(20),
        occurred_at: at,
      });
    }
  }

  function bodyOf(body: unknown): string | null {
    if (!state.options.captureBodies || body === undefined || body === null) return null;
    if (typeof body === 'string') return state.redactor.body(body);
    if (body instanceof URLSearchParams) return state.redactor.body(body.toString());
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const o: Record<string, string> = {};
      body.forEach((v, k) => (o[k] = typeof v === 'string' ? v : `[file ${v.name}]`));
      return state.redactor.body(o);
    }
    if (body instanceof Blob) return `[blob ${body.type} ${body.size}B]`;
    if (body instanceof ArrayBuffer) return `[binary ${body.byteLength}B]`;
    return '[unsupported body]';
  }

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = absoluteUrl(input instanceof Request ? input.url : String(input));

    if (state.isOwnApi(url) || !state.shouldTrace(url)) return originalFetch.call(window, input, init);

    const traceId = randomId(32);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set(TRACE_HEADER, traceId);
    const reqHeaders: Record<string, string> = {};
    headers.forEach((v, k) => (reqHeaders[k] = v));
    const request: Exchange = { headers: state.redactor.headersOf(reqHeaders), body: bodyOf(init?.body) };

    const started = performance.now();
    let response: Response;
    try {
      response = await originalFetch.call(window, input, { ...init, headers });
    } catch (err) {
      record({ method, url, status: 0, duration_ms: Math.round(performance.now() - started), trace_id: traceId, request, error: `Network error: ${(err as Error).message}` });
      throw err;
    }
    const duration = Math.round(performance.now() - started);

    let resBody: string | null = null;
    const resHeaders: Record<string, string> = {};
    response.headers.forEach((v, k) => (resHeaders[k] = v));
    if (response.status >= 400 && state.options.captureBodies && TEXT_TYPES.test(response.headers.get('content-type') ?? '')) {
      try {
        resBody = state.redactor.body(await response.clone().text());
      } catch {
        resBody = null;
      }
    }
    record({ method, url, status: response.status, duration_ms: duration, trace_id: traceId, request, response: { headers: state.redactor.headersOf(resHeaders), body: resBody } });
    return response;
  };

  type Meta = { method: string; url: string; headers: Record<string, string>; traceId?: string; started: number; traced: boolean };
  const meta = new WeakMap<XMLHttpRequest, Meta>();

  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    const abs = absoluteUrl(String(url));
    meta.set(this, { method: method.toUpperCase(), url: abs, headers: {}, started: 0, traced: !state.isOwnApi(abs) && state.shouldTrace(abs) });
    return (originalOpen as (...a: unknown[]) => void).apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (this: XMLHttpRequest, name: string, value: string) {
    const m = meta.get(this);
    if (m) m.headers[name] = value;
    return originalSetHeader.call(this, name, value);
  };

  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const m = meta.get(this);
    if (m && m.traced) {
      m.traceId = randomId(32);
      m.headers[TRACE_HEADER] = m.traceId;
      originalSetHeader.call(this, TRACE_HEADER, m.traceId);
      m.started = performance.now();
      const request: Exchange = { headers: state.redactor.headersOf(m.headers), body: bodyOf(body) };
      this.addEventListener('loadend', () => {
        const duration = Math.round(performance.now() - m.started);
        const resHeaders: Record<string, string> = {};
        (this.getAllResponseHeaders() || '')
          .trim()
          .split(/[\r\n]+/)
          .forEach((line) => {
            const i = line.indexOf(':');
            if (i > 0) resHeaders[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
          });
        let resBody: string | null = null;
        if (this.status >= 400 && state.options.captureBodies && (this.responseType === '' || this.responseType === 'text')) {
          try {
            resBody = state.redactor.body(this.responseText);
          } catch {
            resBody = null;
          }
        }
        record({
          method: m.method,
          url: m.url,
          status: this.status,
          duration_ms: duration,
          trace_id: m.traceId,
          request,
          response: { headers: state.redactor.headersOf(resHeaders), body: resBody },
          error: this.status === 0 ? 'Network error' : undefined,
        });
      });
    }
    return originalSend.call(this, body);
  };

  return () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.open = originalOpen;
    XMLHttpRequest.prototype.send = originalSend;
    XMLHttpRequest.prototype.setRequestHeader = originalSetHeader;
  };
}
